import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { EmptyState } from "../empty-state";

import type { Column } from "./types";

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  page: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}

export function DataTableDesktopView<T>({
  columns,
  rows,
  loading,
  page,
  totalPages,
  totalCount,
  onPageChange,
}: Props<T>) {
  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead
                  key={c.key}
                  className={c.headerClassName ?? c.className}
                >
                  {c.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    Memuat...
                  </p>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <EmptyState />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, i) => (
                <TableRow key={i}>
                  {columns.map((c) => (
                    <TableCell key={c.key} className={c.className}>
                      {c.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{totalCount} data</span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => onPageChange(1)}
            >
              «
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => onPageChange(page - 1)}
            >
              ‹
            </Button>
            {/* key resets the draft whenever the page changes from elsewhere */}
            <PageJump
              key={page}
              page={page}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              ›
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => onPageChange(totalPages)}
            >
              »
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

function PageJump({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const [draft, setDraft] = useState(String(page));

  function commit() {
    const next = Math.min(totalPages, Math.max(1, Number(draft)));
    if (draft && next !== page) onPageChange(next);
    else setDraft(String(page));
  }

  return (
    <span className="flex items-center gap-1 px-1">
      <Input
        type="text"
        inputMode="numeric"
        aria-label="Nomor halaman"
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, ""))}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        onFocus={(e) => e.currentTarget.select()}
        className="h-8 w-12 px-1 text-center md:text-sm"
      />
      <span>/ {totalPages}</span>
    </span>
  );
}
